import { motion } from "framer-motion";
import {
  Building2,
  Utensils,
  Users,
  TrendingUp,
  CheckCircle2,
  Award,
  Rocket,
  Target
} from "lucide-react";
import { useRegion } from "../../../regions/RegionContext";

const AboutSection = () => {
  const { region } = useRegion();
  const kitchensLabel = region === "usa" ? "Ghost Kitchens" : "Cloud Kitchens";
  const services = [
    {
      text: "Complete restaurant setup from concept to operations",
      icon: Building2
    },
    {
      text: "Food business consulting for Cafes, QSRs, and more",
      icon: Utensils
    },
    {
      text: "Management contract services for hands-free operations",
      icon: Users
    },
    {
      text: "Restaurant turnaround solutions for struggling outlets",
      icon: TrendingUp
    }
  ];

  const customServices = [
    { text: "Restaurant Concept Development", icon: Rocket },
    { text: "Menu Engineering & Innovation", icon: Utensils },
    { text: "Staff Management & Development", icon: Users },
    { text: "Operational Audit & Optimization", icon: Target }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: (index) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: index * 0.1,
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };

  return (
    <section className="relative py-24 md:py-32 px-6 bg-white overflow-hidden">
      {/* Enhanced Background decoration */}
      <div className="absolute inset-0 opacity-[0.015]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-20 left-0 w-[500px] h-[500px] bg-primary rounded-full blur-3xl" 
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.04, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-yellow-400 rounded-full blur-3xl" 
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-20 md:mb-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
              Our Company
            </span>
          </motion.div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.1] mb-6"
            style={{ letterSpacing: '-0.02em' }}
          >
            Restaurant
            <br />
            Consulting{" "}
            <span className="relative inline-block">
              <span className="relative z-10 italic font-serif" style={{ color: "#1b7840" }}>Services</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 8C50 2, 150 2, 198 8"
                  stroke="#1b7840"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                />
              </svg>
            </span>
          </h2>

        </motion.div>

        {/* Main Content - Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mb-16">
          {/* Left Column - Story */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6"
          >
            <div className="bg-gradient-to-br from-primary/5 to-transparent p-8 md:p-10 rounded-3xl border border-primary/10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Our Story</h3>
              </div>

              <div className="space-y-5 text-gray-700 text-base md:text-lg leading-relaxed">
                <p>
                  <span className="font-semibold text-primary">Terces Solutions</span> is a results-driven restaurant consulting firm founded in 2022 by{" "}
                  <span className="font-semibold text-primary">Sudeesh Pabbathi</span>{" "}
                  and <span className="font-semibold text-primary">Manoj Thammu</span>, built on a deep understanding of how food businesses actually succeed—not just operate.
                </p>

                <p>
                  Most restaurants fail not because of effort, but because of <span className="font-semibold text-primary">fragmented thinking, disconnected decisions</span> across menu, pricing, operations, and marketing - Terces exists to solve that.
                </p>

                <p>
                  We bring a <span className="font-semibold text-primary">holistic, system-driven consulting approach</span> that integrates every critical function of a food business—turning ideas into structured, scalable, and profitable ventures.
                </p>

                <p>
                  From concept to execution, we partner with entrepreneurs and operators to <span className="font-semibold text-primary">build high-performance food businesses</span> across formats including Cafés, QSRs, Food Kiosks, and {kitchensLabel}.
                </p>

                <p className=" text-gray-900">
                  We don't just help you start.<br />
                  We build systems that help you <span className="font-semibold text-primary">scale, sustain, and win.</span>
                </p>
              </div>
            </div>

        
          </motion.div>

          {/* Right Column - Services */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="sticky top-24">
              <h4 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900">
                Our Consulting Services Include:
              </h4>

              <div className="space-y-4 mb-10">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <motion.div
                      key={index}
                      custom={index}
                      variants={cardVariants}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      whileHover={{ 
                        x: 8,
                        transition: { duration: 0.2 }
                      }}
                      className="group flex items-start gap-4 px-6 py-5 rounded-2xl bg-[#f6f4ef] hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-primary/10"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white group-hover:bg-primary/10 flex items-center justify-center transition-colors duration-300">
                        <Icon className="w-6 h-6 text-primary" strokeWidth={2} />
                      </div>
                      <div className="flex-1 pt-1">
                        <span className="text-gray-800 group-hover:text-gray-900 text-base leading-relaxed transition-colors">
                          {service.text}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Custom Consulting Packages */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-8 border-2 border-primary/20"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                    <Rocket className="w-5 h-5 text-white" />
                  </div>
                  <h5 className="text-xl md:text-2xl font-bold text-gray-900">
                    Custom Consulting Packages
                  </h5>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {customServices.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.05 }}
                        className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm hover:shadow-md transition-allcursor-pointer"
                      >
                        <Icon className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={2} />
                        <span className="text-gray-700 text-sm leading-tight">
                          {service.text}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Closing Statement - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden"
        >
          <div
            className="relative text-white rounded-3xl p-4 md:p-7 text-center"
            style={{ background: 'linear-gradient(135deg, #155a30 0%, #0d3d1f 100%)' }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="relative z-10"
            >
              <p className="text-sm md:text-lg lg:text-xl leading-relaxed font-medium max-w-4xl mx-auto">
                Whether you're a{" "}
                <motion.span 
                  className="font-bold border-b-2 border-white inline-block"
                  whileHover={{ scale: 1.05 }}
                >
                  first-time restaurateur
                </motion.span>{" "}
                or a{" "}
                <motion.span 
                  className="font-bold border-b-2 border-white inline-block"
                  whileHover={{ scale: 1.05 }}
                >
                  seasoned brand
                </motion.span>{" "}
                looking to expand, Terces Solutions brings{" "}
                <span className="text-yellow-300 font-bold">innovation</span> and{" "}
                <span className="text-yellow-300 font-bold">industry insight</span>{" "}
                to ensure your success.
              </p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="mt-4"
              >
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-block px-8 py-3 bg-white text-primary rounded-full font-semibold text-base shadow-xl hover:shadow-2xl transition-all duration-300"
                >
                  Start Your Journey
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;